import { Type } from "@angular/core";
import { DashboardWidget } from "./dashboard.types";
import { BeforeStepChangeResult, WizardAction } from "./wizard.types";

export interface IModuleTabConfigInfo {
  ActualWidgetIndex?: number;
  Items?: ModuleTabItem[];
  Widgets?: (DashboardWidget | undefined)[];
  WidgetFactories?: Array<(() => DashboardWidget | undefined) | undefined>;
  Layout?: 'left' | 'top' | 'timeline-left' | 'timeline-top';
  HideTabs?: boolean;
  Wizard?: IModuleWizardConfig;
}

export interface IModuleWizardConfig {
  Title?: string;
  Subtitle?: string;
  Icon?: string;
  OnClose?: () => void;
  OnBeforeClose?: () => BeforeStepChangeResult;
  ShowHeader?: boolean;
  ShowNavigationButtons?: boolean;
  WizardInstanceId?: string;
  InitialEntityData?: any;
  HeaderComponent?: Type<any>;
  HeaderComponentInputs?: Record<string, any>;
  ButtonTexts?: {
    Previous?: string;
    Next?: string;
    NextInProgress?: string;
    Skip?: string;
    Complete?: string;
    SaveAsDraft?: string;
  };
  /** Material icon name for the complete/submit button (default: check). */
  CompleteIcon?: string;
  ShowSaveAsDraftButton?: boolean;
  OnSaveAsDraft?: () => void;
  /**
   * Callback appelé avant chaque changement d'étape (next/previous).
   * Permet de sauvegarder automatiquement les données avant la navigation.
   * Retourne true pour continuer la navigation, false pour l'annuler.
   */
  OnBeforeStepChange?: (
    currentStep: number,
    targetStep: number,
    action: WizardAction,
    wizardInstanceId: string
  ) => BeforeStepChangeResult;
  CanNavigateToStep?: (stepIndex: number) => boolean;
  /** When true, all wizard timeline steps stay visually active (Academy incident pattern). */
  DisableStepLocking?: boolean;
}

export interface IModuleTabItemInfo {
  Key: string;
  Label: string;
  IsLongLabel: boolean;
  LongLabel: string;
  IsNumberDisplayed: boolean;
  Number: number;
  Icon?: string;
  IsSkippable?: boolean;
  // Statut des boutons de navigation pour cet onglet
  CanGoNext?: boolean;
  CanGoPrevious?: boolean;
  // Indique si l'étape a déjà été chargée une première fois
  HasBeenLoaded?: boolean;
}

export class ModuleTabConfig {
  ActualWidgetIndex: number;
  Items?: ModuleTabItem[];
  Widgets?: (DashboardWidget | undefined)[];
  WidgetFactories?: Array<(() => DashboardWidget | undefined) | undefined>;
  Layout?: 'top' | 'left' | 'timeline-left' | 'timeline-top';
  HideTabs?: boolean;
  Wizard?: IModuleWizardConfig;

  constructor(config: IModuleTabConfigInfo) {
    this.Items = config.Items ?? [];
    this.Widgets = config.Widgets ?? [];
    this.WidgetFactories = config.WidgetFactories ?? [];
    this.ActualWidgetIndex = config.ActualWidgetIndex ?? 0;
    this.Layout = config.Layout ?? 'top';
    this.HideTabs = config.HideTabs ?? false;
    this.Wizard = config.Wizard;
  }

  next(): DashboardWidget | null {
    if (this.Widgets) {
      return this.Widgets[this.ActualWidgetIndex] ?? null;
    }

    return null;
  }

  // Méthodes pour le mode wizard
  canGoNext(): boolean {
    const currentItem = this.Items?.[this.ActualWidgetIndex];
    if (currentItem?.CanGoNext !== undefined) {
      return currentItem.CanGoNext;
    }
    // Fallback sur la logique par défaut
    return this.ActualWidgetIndex < (this.Items?.length || 0) - 1;
  }

  canGoPrevious(): boolean {
    const currentItem = this.Items?.[this.ActualWidgetIndex];
    if (currentItem?.CanGoPrevious !== undefined) {
      return currentItem.CanGoPrevious;
    }
    // Fallback sur la logique par défaut
    return this.ActualWidgetIndex > 0;
  }

  canSkip(): boolean {
    const currentItem = this.Items?.[this.ActualWidgetIndex];
    return this.canGoNext() && (currentItem?.IsSkippable ?? false);
  }

  isLastStep(): boolean {
    return this.ActualWidgetIndex === (this.Items?.length || 0) - 1;
  }

  isFirstStep(): boolean {
    return this.ActualWidgetIndex === 0;
  }

  isWizardMode(): boolean {
    return this.Wizard != null;
  }

  showNavigationButtons(): boolean {
    return this.Wizard?.ShowNavigationButtons ?? true;
  }

  showSaveAsDraftButton(): boolean {
    return this.Wizard?.ShowSaveAsDraftButton ?? false;
  }

  getButtonText(buttonType: 'previous' | 'next' | 'nextInProgress' | 'skip' | 'complete' | 'saveAsDraft'): string {
    const defaultTexts: Record<string, string> = {
      previous: 'app.wizard.previous',
      next: 'app.wizard.next',
      nextInProgress: 'app.shared.executioninprogress',
      skip: 'app.wizard.skip',
      complete: 'app.wizard.complete',
      saveAsDraft: 'app.wizard.saveasdraft'
    };

    const key = buttonType === 'saveAsDraft'
      ? 'SaveAsDraft'
      : buttonType === 'nextInProgress'
        ? 'NextInProgress'
        : buttonType.charAt(0).toUpperCase() + buttonType.slice(1);
    return this.Wizard?.ButtonTexts?.[key as keyof typeof this.Wizard.ButtonTexts] ?? defaultTexts[buttonType];
  }

  getCompleteIcon(): string {
    return this.Wizard?.CompleteIcon ?? 'check';
  }

  static createInstance(info: IModuleTabConfigInfo | undefined): ModuleTabConfig {
    return new ModuleTabConfig(<IModuleTabConfigInfo>{
      ActualWidgetIndex: info?.ActualWidgetIndex,
      Items: info?.Items,
      Widgets: info?.Widgets,
      WidgetFactories: info?.WidgetFactories,
      Layout: info?.Layout,
      HideTabs: info?.HideTabs,
      Wizard: info?.Wizard
    });
  }

  /**
   * Crée une configuration de base pour un module tab avec un widget
   * @param component - Le composant à afficher dans le widget
   * @param tabKey - La clé de l'onglet (ex: 'task-template-list')
   * @param tabLabel - Le libellé de l'onglet (ex: 'Modèles de tâches')
   * @param hideTabs - Masquer les onglets (défaut: true)
   * @param showHelpPanel - Afficher le panneau d'aide (défaut: false)
   * @param onEmptyConfiguratorCompleted - Callback appelé quand la configuration vide est terminée (défaut: undefined)
   * @param config - Configuration spécifique à passer au composant (défaut: undefined)
   * @returns ModuleTabConfig configuré
   */
  static create(
    component: Type<any>,
    tabKey: string,
    tabLabel: string,
    hideTabs: boolean = true,
    showHelpPanel: boolean = false,
    onEmptyConfiguratorCompleted?: () => void,
    config?: any
  ): ModuleTabConfig {
    const moduleTabConfig = new ModuleTabConfig(<IModuleTabConfigInfo>{
      ActualWidgetIndex: 0,
      HideTabs: hideTabs
    });

    // Ajouter l'onglet
    moduleTabConfig.Items!.push(new ModuleTabItem(<IModuleTabItemInfo>{
      Key: tabKey,
      Label: tabLabel
    }));

    // Ajouter le widget avec le composant
    const widget = new DashboardWidget(component);
    widget.data.Properties = {
      'IntoTab': true,
      'ShowHelpPanel': showHelpPanel
    };

    // Ajouter le callback si fourni
    if (onEmptyConfiguratorCompleted) {
      widget.data.Properties['onEmptyConfiguratorCompleted'] = onEmptyConfiguratorCompleted;
    }

    // Ajouter la configuration spécifique si fournie
    if (config) {
      widget.data.Properties['Config'] = config;
    }

    moduleTabConfig.Widgets!.push(widget);

    return moduleTabConfig;
  }
}

export class ModuleTabItem {
  Key: string;
  Label: string;
  IsLongLabel: boolean;
  LongLabel: string;
  IsNumberDisplayed: boolean;
  Number: number;
  Icon?: string;
  IsSkippable?: boolean;
  // Statut des boutons de navigation pour cet onglet
  CanGoNext?: boolean;
  CanGoPrevious?: boolean;
  HasBeenLoaded?: boolean;

  constructor(info: IModuleTabItemInfo) {
    this.Key = info.Key;
    this.Label = info.Label;
    this.LongLabel = info.LongLabel;
    this.IsLongLabel = info.IsLongLabel;
    this.IsNumberDisplayed = info.IsNumberDisplayed ? info.IsNumberDisplayed : false;
    this.Number = info.Number ? info.Number : 0;
    this.Icon = info.Icon;
    this.IsSkippable = info.IsSkippable ?? false;
    this.CanGoNext = info.CanGoNext ?? true; // Par défaut, le bouton suivant est activé
    this.CanGoPrevious = info.CanGoPrevious ?? true; // Par défaut, le bouton précédent est activé
    this.HasBeenLoaded = info.HasBeenLoaded ?? false;
  }
}
